import { Card, IconButton, Tooltip } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { api } from "utils/Api";
import SmartTableCard from "utils/Smart Table/SmartTableCard";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UnitsPopupForm from "./UnitsPopupForm";
import DeleteForm from "layouts/admin/Organization/DeleteForm";
import { useHierarchy } from "context/HierarchyContext";
import { showSuccess } from "utils/messageAlerts";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ConfirmDialog from "utils/ConfirmDialog";
import { getSecureItem } from "utils/SecureStorage";

function UnitsData() {
  const [allUnits, setAllUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [popupMode, setPopupMode] = useState("create");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const navigate = useNavigate();
  const { orgId, propertyId, setUnitId} = useHierarchy();
  const { orgCode, propertyCode } = useParams();
  const rolesId = JSON.parse(getSecureItem("currentUser"))?.role_id;
  const [confirmDialog, setConfirmDialog] = useState({
      open: false,
      type: null,
      property: null,
    });

  const fetchUnits = async () => {
    try {
      const response = await api.su_unit.getSuUnits();

      const filtered = response.filter(
        (unit) => unit.property_id === Number(propertyId)
      );

      setAllUnits(filtered);
      setFilteredUnits(filtered);

    } catch (e) {
      setAllUnits([]);
      setFilteredUnits([]);    
    }
  };

  // const handleUnitNameClick = (unit) => {
  //   setUnitId(unit.id)
  //   // Optional: You can set the selected unit here if you want to show details in a sidebar or modal
  //   navigate(`/super-admin/organization/${orgCode}/properties/${propertyCode}/unit/${unit.unit_code}`);
  // } 

  useEffect(() => {
    fetchUnits();
  }, [propertyId]);

  // ================= SEARCH =================
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (!value.trim()) {
      setFilteredUnits(allUnits);
      return;
    }

    const filtered = allUnits.filter((unit) =>
      unit.name.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredUnits(filtered);
  };

  // ================= EDIT =================
  const handleEditUnit = (unit) => {
    setSelectedUnit(unit);
    setPopupMode("edit");
    setIsPopupOpen(true);
  };

  // ================= DELETE =================
  const handleDeleteUnit = async (id) => {
    try {
      await api.su_unit.deleteSuUnit(id);
      showSuccess("Unit Deleted Successfully.");
      fetchUnits();
    } catch (e) {
  
    }
  };

  const handleUnitActionClick = (unit, action) => {
    setConfirmDialog({
      open: true,
      type: action,
      unit,
    });
  };

  const handleUnitActive = async () => {
    const { unit, type } = confirmDialog;
    const isDelete = type === "delete";

    try {
      if (isDelete) {
        await api.su_unit.hardDeleteUnit(unit.id);
        showSuccess("Unit deleted successfully");
      } else {
        await api.su_unit.restoreUnit({
          unit_id: unit.id,
          property_id: unit.property_id,
          current_user_role_id: rolesId,
        });
        showSuccess("Unit restored successfully");
      }

      fetchUnits();
    } catch (error) {
      // console.log('error: ', error);
    }
    setConfirmDialog({ open: false, type: null, property: null });
  };

  return (
    <>
      <Card>
        <SmartTableCard
          title={<h4>Units in Property</h4>}
          headerRight={[
            <SoftButton
              key="add-unit"
              variant="outlined"
              color="info"
              size="small"
              onClick={() => {
                setSelectedUnit(null);
                setIsPopupOpen(true);
              }}
            >
              Add Unit
            </SoftButton>,

            <SoftInput
              key="search-unit"
              placeholder="Search Units"
              value={searchTerm}
              onChange={handleSearch}
              size="small"
            />,
          ]}

          columns={[
            { label: "Unit Name", key: "UnitName" },
            { label: "Description", key: "Description", align: "center" },
            { label: "Unit Code", key: "UnitCode", align: "center" },
            { label: "Status", key: "Status", align: "center" },
            { label: "Active", key: "Active", align: "center" },
            { label: "Actions", key: "Actions", align: "center" },
          ]}

          rows={filteredUnits.map((unit) => {
            const isActive = unit.active === true;
            const isInactive = !isActive;
            return {
              UnitName: (
                <SoftTypography variant="button" sx={{ fontWeight: 600 }}>
                  {unit.name}
                </SoftTypography>
              ),
              Description: unit.description,
              UnitCode: unit.unit_code,
              Status: unit.active ? "Active" : "Inactive",
              Active: isActive ? (
                <SoftTypography variant="button" color="success" fontWeight="bold">
                  Yes
                </SoftTypography>
              ) : (
                <SoftBox display="flex" justifyContent="center" gap="6px">
                  {/* Restore */}
                  <Tooltip title="Restore Unit" arrow>
                    <IconButton
                      onClick={() => handleUnitActionClick(unit, "restore")}
                      sx={{
                        width: 30,
                        height: 30,
                        color: "#16A34A",
                        backgroundColor: "#ECFDF5",
                        "&:hover": {
                          backgroundColor: "#D1FAE5",
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      <RestoreIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>

                  {/* Delete */}
                  <Tooltip title="Delete Permanently" arrow>
                    <IconButton
                      onClick={() => handleUnitActionClick(unit, "delete")}
                      sx={{
                        width: 30,
                        height: 30,
                        color: "#DC2626",
                        backgroundColor: "#FEF2F2",
                        "&:hover": {
                          backgroundColor: "#FEE2E2",
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      <DeleteForeverIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </SoftBox>
              ),
              Actions: (
                <SoftBox display="flex" justifyContent="center" gap="8px">
                  <IconButton
                    onClick={() => handleEditUnit(unit)}
                    disabled={isInactive}
                    sx={{
                      border: "1px solid #D0D5DD",
                      backgroundColor: "#F9FAFB",
                      width: 34,
                      height: 34,
                      "& svg": { fontSize: 18 },
                      color: "#475467",
                      "&:hover": {
                        backgroundColor: "#E0F2FE",
                        borderColor: "#38BDF8",
                        color: "#0284C7",
                      },
                    }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setSelectedUnit(unit);
                      setDeletePopupOpen(true);
                    }}
                    disabled={isInactive}
                    sx={{
                      border: "1px solid #FECACA",
                      backgroundColor: "#FEF2F2",
                      width: 34,
                      height: 34,
                      "& svg": { fontSize: 18 },
                      color: "#B91C1C",
                      "&:hover": {
                        backgroundColor: "#FEE2E2",
                        borderColor: "#F87171",
                        color: "#991B1B",
                      },
                    }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </SoftBox>
              ),
            };
          })} />
      </Card>

      <ConfirmDialog
        open={confirmDialog.open}
        type={confirmDialog.type}
        title={confirmDialog.type === "delete" ? "Delete Unit" : "Restore Unit"}
        description={
          confirmDialog.type === "delete"
            ? "This will permanently delete the unit. This action cannot be undone."
            : "Are you sure you want to restore this unit?"
        }
        confirmText={confirmDialog.type === "delete" ? "Delete Permanently" : "Restore"}
        onClose={() => setConfirmDialog({ open: false, type: null, unit: null })}
        onConfirm={handleUnitActive}
      />

      {isPopupOpen && (
        <UnitsPopupForm
          mode={popupMode}
          unit={selectedUnit}
          refreshUnits={fetchUnits}
          propertyId={propertyId}
          onClose={() => {
            setIsPopupOpen(false);
          }}
          orgId={orgId}
        />
      )}

      {deletePopupOpen && (
        <DeleteForm
          data={selectedUnit}
          type="Unit"
          displayField="name"
          onClose={() => setDeletePopupOpen(false)}
          onDelete={async (id) => {
            await handleDeleteUnit(id);
            setDeletePopupOpen(false);
          }}
        />
      )}
    </>
  );
}


export default UnitsData;

